import React, { useEffect, useMemo, useState } from 'react';
import DashboardHeader from '../layouts/dashboard_header';
import SideMenu from '../layouts/dashboard_menu';
import Canvas from '../layouts/dahboard_canvas';
import DashboardFooter from '../layouts/dashboard_footer';
import axios from 'axios';
import localforage from 'localforage';
import { useBaseUrl } from '../../utils/utils';
import { Link } from 'react-router-dom';

const ACCENT = '#0f766e';
const ACCENT_SOFT = 'rgba(15, 118, 110, 0.10)';

interface Facility {
    id: number;
    name: string;
    email: string;
    phone?: string;
    address?: string;
    type?: string;
    status?: string;
    staff_count?: number;
    created_at?: string;
    town?: string;
    county?: string;
    category?: string;
    active?: boolean;
}

interface StaffMember {
    id?: number;
    first_name?: string;
    last_name?: string;
    email: string;
    role?: string;
    specialization?: string;
    status?: string | boolean;
    avatar?: string;
    clinic_name?: string;
}

type Tab = 'overview' | 'facilities' | 'staff';

const InstitutionManagement: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('overview');
    const [isLoading, setIsLoading] = useState(true);
    const [instName, setInstName] = useState('');
    const [instEmail, setInstEmail] = useState('');
    const [instId, setInstId] = useState<number>(0);
    const [clinicName, setClinicName] = useState('');
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [staff, setStaff] = useState<StaffMember[]>([]);
    const [staffSearch, setStaffSearch] = useState('');
    const [facilitySearch, setFacilitySearch] = useState('');
    const [dashboardData, setDashboardData] = useState<any>(null);
    const base_url = useBaseUrl();

    useEffect(() => {
        document.title = "Institution Management - Uzima";
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [clinic_id, institution_id, staff_id, iName, iEmail, cName] = await Promise.all([
                localforage.getItem("clinic_id"),
                localforage.getItem("institution_id"),
                localforage.getItem("staff_id"),
                localforage.getItem("institution_name"),
                localforage.getItem("clinic_email"),
                localforage.getItem("clinic_name"),
            ]);
            setInstName(typeof iName === 'string' ? iName : '');
            setInstEmail(typeof iEmail === 'string' ? iEmail : '');
            setInstId(institution_id as number || 0);
            setClinicName(typeof cName === 'string' ? cName : '');

            try {
                const facRes = await axios.post(`${base_url}facilities/clinics/switchable`, {
                    staff_id,
                    clinic_id,
                }, { timeout: 10000 });
                const d = facRes.data;
                if (d?.status === 'success' && Array.isArray(d.data?.facilities)) {
                    setFacilities(d.data.facilities.map((item: any) => ({
                        id: item.id,
                        name: item.name,
                        email: item.email,
                        phone: item.phone,
                        town: item.town,
                        type: item.category,
                        category: item.category,
                        status: item.is_current ? 'Active' : (item.active ? 'Available' : 'Inactive'),
                        active: item.active,
                        staff_count: item.staff_count,
                    })));
                } else {
                    setFacilities([]);
                }
            } catch (e) { console.error("Error loading facilities:", e); setFacilities([]); }

            try {
                const staffRes = await axios.post(`${base_url}facilities/clinic-list`, { institution_id }, { timeout: 10000 });
                const d = staffRes.data;
                if (d?.status === 'success' && Array.isArray(d.data)) {
                    setStaff(d.data.map((item: any) => ({
                        id: item.user_id,
                        first_name: item.fname,
                        last_name: item.lname,
                        email: item.email,
                        role: item.title,
                        specialization: item.title,
                        status: item.status,
                        avatar: item.avatar,
                        clinic_name: item.clinic,
                    })));
                } else {
                    setStaff([]);
                }
            } catch (e) { console.error("Error loading staff:", e); setStaff([]); }

            // Load dashboard overview
            try {
                const dashRes = await axios.get(`${base_url}facilities/dashboard/facilities`, { timeout: 10000 });
                if (dashRes.data) setDashboardData(dashRes.data);
            } catch (e) { console.error("Error loading dashboard:", e); }

        } catch (e) {
            console.error("Error loading data:", e);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredStaff = staff.filter(s => {
        if (!staffSearch) return true;
        const term = staffSearch.toLowerCase();
        return s.first_name?.toLowerCase().includes(term) || s.last_name?.toLowerCase().includes(term) || s.email?.toLowerCase().includes(term) || s.role?.toLowerCase().includes(term);
    });

    const filteredFacilities = facilities.filter(f => {
        if (!facilitySearch) return true;
        const term = facilitySearch.toLowerCase();
        return f.name?.toLowerCase().includes(term) || f.email?.toLowerCase().includes(term) || f.type?.toLowerCase().includes(term);
    });

    const tabs: { id: Tab; label: string; icon: string }[] = [
        { id: 'overview', label: 'Overview', icon: 'bx bx-buildings' },
        { id: 'facilities', label: 'Facilities', icon: 'bx bx-building-house' },
        { id: 'staff', label: 'Staff', icon: 'bx bx-user-plus' },
    ];

    const activeFacility = useMemo(() => facilities.find((facility) => facility.name === clinicName) || facilities[0], [clinicName, facilities]);

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">

                        {/* Header */}
                        <div className="sc-header">
                            <div>
                                <h1><i className="bx bx-buildings me-2" style={{ color: ACCENT }}></i>Institution Management</h1>
                                <p>Manage facilities, staff, and organization structure</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="sc-btn sc-btn-ghost" onClick={loadData}><i className="bx bx-refresh"></i> Refresh</button>
                                <Link to="/institution/add-clinic" className="sc-btn sc-btn-primary" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Add Facility</Link>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(15,23,42,0.03)', padding: 4, borderRadius: 12 }}>
                            {tabs.map(t => (
                                <button key={t.id} onClick={() => setActiveTab(t.id)}
                                    style={{
                                        padding: '8px 18px', borderRadius: 10, border: 'none',
                                        background: activeTab === t.id ? '#fff' : 'transparent',
                                        color: activeTab === t.id ? ACCENT : 'var(--uzima-shell-text-muted)',
                                        fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                                        boxShadow: activeTab === t.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                                        transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
                                    }}
                                >
                                    <i className={t.icon}></i> {t.label}
                                </button>
                            ))}
                        </div>

                        {/* Stats */}
                        <div className="sc-stats" style={{ marginBottom: 20 }}>
                            {[
                                { label: 'Facilities', val: facilities.length, icon: 'bx bx-building-house', cls: 'teal' },
                                { label: 'Staff Members', val: staff.length, icon: 'bx bx-user-plus', cls: 'blue' },
                                { label: 'Active', val: staff.filter(s => s.status === true || s.status === 'active' || !s.status).length, icon: 'bx bx-check-circle', cls: 'amber' },
                                { label: 'Current Team', val: activeFacility?.staff_count || 0, icon: 'bx bx-category', cls: 'violet' },
                            ].map((s, i) => (
                                <div className="sc-stat" key={i}>
                                    <div className={`sc-stat-icon ${s.cls}`}><i className={s.icon}></i></div>
                                    <div className="sc-stat-value">{s.val}</div>
                                    <div className="sc-stat-label">{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                    {/* Institution Info */}
                                    <div className="sc-card" style={{ margin: 0 }}>
                                        <div style={{ padding: '12px 18px', borderBottom: `2px solid ${ACCENT}`, background: 'rgba(15,118,110,0.03)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-buildings me-2" style={{ color: ACCENT }}></i>Organization Details</div>
                                        </div>
                                        <div className="sc-card-body">
                                            {isLoading ? (
                                                <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>Loading...</p>
                                            ) : (
                                                <>
                                                    {[
                                                        { label: 'Institution', value: instName || 'Not set' },
                                                        { label: 'Active Facility', value: clinicName || 'Not set' },
                                                        { label: 'Email', value: instEmail || 'Not set' },
                                                        { label: 'Facilities', value: `${facilities.length} accessible` },
                                                        { label: 'Active team', value: `${activeFacility?.staff_count || 0} members in current facility` },
                                                    ].map((item, i) => (
                                                        <div key={i} style={{ marginBottom: 12 }}>
                                                            <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', marginBottom: 2 }}>{item.label}</div>
                                                            <div style={{ fontSize: '0.86rem', fontWeight: 600 }}>{item.value}</div>
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="sc-card" style={{ margin: 0 }}>
                                        <div style={{ padding: '12px 18px', borderBottom: '2px solid #3b82f6', background: 'rgba(59,130,246,0.03)' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-rocket me-2" style={{ color: '#3b82f6' }}></i>Quick Actions</div>
                                        </div>
                                        <div className="sc-card-body">
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                                {[
                                                    { label: 'Add Facility', icon: 'bx bx-building-house', path: '/institution/add-clinic', color: ACCENT },
                                                    { label: 'Add Staff', icon: 'bx bx-user-plus', path: '/institution/add-staff', color: '#3b82f6' },
                                                    { label: 'View Clinics', icon: 'bx bx-list-ul', path: '/institution/clinics', color: '#8b5cf6' },
                                                    { label: 'Professionals', icon: 'bx bx-id-card', path: '/institution/professionals', color: '#d97706' },
                                                    { label: 'Settings', icon: 'bx bx-cog', path: '/institution/settings', color: '#64748b' },
                                                    { label: 'Profile', icon: 'bx bx-user-circle', path: '/institution/profile', color: '#e11d48' },
                                                ].map((item, i) => (
                                                    <Link key={i} to={item.path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, textDecoration: 'none', background: `${item.color}08`, border: `1px solid ${item.color}20`, transition: 'all 0.15s' }}
                                                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = `${item.color}15`; }}
                                                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = `${item.color}08`; }}
                                                    >
                                                        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${item.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className={item.icon} style={{ color: item.color, fontSize: '0.9rem' }}></i>
                                                        </div>
                                                        <span style={{ fontWeight: 600, fontSize: '0.78rem', color: 'var(--uzima-shell-text)' }}>{item.label}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Recent Facilities */}
                                <div className="sc-card">
                                    <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--uzima-shell-border)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontWeight: 700, fontSize: '0.84rem' }}><i className="bx bx-building-house me-2" style={{ color: ACCENT }}></i>Facilities ({facilities.length})</div>
                                            <button className="sc-btn sc-btn-ghost sc-btn-sm" onClick={() => setActiveTab('facilities')}><i className="bx bx-right-arrow-alt"></i> View All</button>
                                        </div>
                                    </div>
                                    <div className="sc-card-body">
                                        {facilities.length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '30px 0' }}>
                                                <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>No facilities registered yet.</p>
                                                <Link to="/institution/add-clinic" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Add Facility</Link>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                                                {facilities.slice(0, 6).map((f, i) => (
                                                    <div key={i} style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid var(--uzima-shell-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        <div style={{ width: 36, height: 36, borderRadius: 10, background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <i className="bx bx-building-house" style={{ color: ACCENT, fontSize: '0.95rem' }}></i>
                                                        </div>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div style={{ fontWeight: 700, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                                                            <div style={{ fontSize: '0.7rem', color: 'var(--uzima-shell-text-muted)' }}>{f.email || f.type || 'Clinic'}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Facilities Tab */}
                        {activeTab === 'facilities' && (
                            <>
                                <div className="sc-card" style={{ marginBottom: 16 }}>
                                    <div className="sc-card-body" style={{ padding: '14px 20px', display: 'flex', gap: 12, alignItems: 'center' }}>
                                        <div className="sc-search" style={{ flex: 1, marginBottom: 0 }}>
                                            <i className="bx bx-search"></i>
                                            <input type="text" placeholder="Search facilities..." value={facilitySearch} onChange={e => setFacilitySearch(e.target.value)} />
                                        </div>
                                        <Link to="/institution/add-clinic" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-plus"></i> Add Facility</Link>
                                    </div>
                                </div>
                                {filteredFacilities.length === 0 ? (
                                    <div className="sc-card">
                                        <div className="sc-card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
                                            <div style={{ width: 60, height: 60, borderRadius: '50%', background: ACCENT_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                                <i className="bx bx-building-house" style={{ fontSize: '1.5rem', color: ACCENT }}></i>
                                            </div>
                                            <p style={{ fontWeight: 700 }}>No Facilities Found</p>
                                            <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                {facilitySearch ? 'No facilities match your search.' : 'No facilities registered yet.'}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                                        {filteredFacilities.map((f, i) => (
                                            <div key={i} className="sc-card" style={{ margin: 0, transition: 'all 0.15s' }}
                                                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
                                                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = ''; }}
                                            >
                                                <div className="sc-card-body" style={{ padding: '16px 18px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                                                        <div style={{ width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg, ${ACCENT}, #14b8a6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <i className="bx bx-building-house" style={{ color: '#fff', fontSize: '1.1rem' }}></i>
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{f.name}</div>
                                                            <div style={{ fontSize: '0.72rem', color: 'var(--uzima-shell-text-muted)' }}>{f.email || 'No email'}</div>
                                                        </div>
                                                        <span style={{ padding: '3px 10px', borderRadius: 20, background: ACCENT_SOFT, color: ACCENT, fontSize: '0.65rem', fontWeight: 700 }}>{f.status || 'Active'}</span>
                                                    </div>
                                                    {f.phone && <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)', marginBottom: 4 }}><i className="bx bx-phone me-1"></i>{f.phone}</div>}
                                                    {f.address && <div style={{ fontSize: '0.76rem', color: 'var(--uzima-shell-text-muted)' }}><i className="bx bx-map me-1"></i>{f.address}</div>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Staff Tab */}
                        {activeTab === 'staff' && (
                            <>
                                <div className="sc-card" style={{ marginBottom: 16 }}>
                                    <div className="sc-card-body" style={{ padding: '14px 20px', display: 'flex', gap: 12, alignItems: 'center' }}>
                                        <div className="sc-search" style={{ flex: 1, marginBottom: 0 }}>
                                            <i className="bx bx-search"></i>
                                            <input type="text" placeholder="Search staff by name, email, or role..." value={staffSearch} onChange={e => setStaffSearch(e.target.value)} />
                                        </div>
                                        <Link to="/institution/add-staff" className="sc-btn sc-btn-primary sc-btn-sm" style={{ textDecoration: 'none' }}><i className="bx bx-user-plus"></i> Add Staff</Link>
                                    </div>
                                </div>
                                {filteredStaff.length === 0 ? (
                                    <div className="sc-card">
                                        <div className="sc-card-body" style={{ textAlign: 'center', padding: '50px 20px' }}>
                                            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                                                <i className="bx bx-user-plus" style={{ fontSize: '1.5rem', color: '#3b82f6' }}></i>
                                            </div>
                                            <p style={{ fontWeight: 700 }}>No Staff Found</p>
                                            <p style={{ fontSize: '0.82rem', color: 'var(--uzima-shell-text-muted)' }}>
                                                {staffSearch ? 'No staff members match your search.' : 'No staff registered yet.'}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="sc-card" style={{ overflow: 'hidden' }}>
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                                <thead>
                                                    <tr style={{ background: 'rgba(15,23,42,0.025)' }}>
                                                        {['Name', 'Email', 'Role', 'Specialization', 'Facility', 'Status'].map((h, i) => (
                                                            <th key={i} style={{ padding: '10px 14px', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--uzima-shell-text-muted)', borderBottom: '1px solid var(--uzima-shell-border)' }}>{h}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredStaff.map((s, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid var(--uzima-shell-border)' }}>
                                                            <td style={{ padding: '10px 14px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg, ${ACCENT}, #14b8a6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '0.6rem', flexShrink: 0 }}>
                                                                        {(s.first_name?.charAt(0) || '') + (s.last_name?.charAt(0) || '')}
                                                                    </div>
                                                                    <span style={{ fontWeight: 600 }}>{s.first_name} {s.last_name}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '10px 14px', color: 'var(--uzima-shell-text-muted)' }}>{s.email}</td>
                                                            <td style={{ padding: '10px 14px' }}>
                                                                <span style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(59,130,246,0.1)', color: '#2563eb', fontSize: '0.68rem', fontWeight: 700 }}>{s.role || 'Staff'}</span>
                                                            </td>
                                                            <td style={{ padding: '10px 14px' }}>{s.specialization || '—'}</td>
                                                            <td style={{ padding: '10px 14px' }}>{s.clinic_name || '—'}</td>
                                                            <td style={{ padding: '10px 14px' }}>
                                                                <span style={{ padding: '3px 10px', borderRadius: 20, background: s.status === true || s.status === 'active' || !s.status ? ACCENT_SOFT : 'rgba(245,158,11,0.14)', color: s.status === true || s.status === 'active' || !s.status ? ACCENT : '#b45309', fontSize: '0.68rem', fontWeight: 700 }}>{s.status === true || s.status === 'active' || !s.status ? 'Active' : 'Pending'}</span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                    </div>
                </div>
            </div>
            <Canvas />
            <DashboardFooter />
        </>
    );
};

export default InstitutionManagement;
